/**
 * Main Settings Modal - Claude-style settings interface
 * Contains sidebar navigation and renders appropriate settings section based on active tab
 */
import React from 'react';
import Modal from '../common/Modal';
import { useSettingsStore } from '../../stores/settingsStore';
import { useAuthStore } from '../../stores/authStore';
import ProfileSettings from './ProfileSettings';
import TheologicalSettings from './TheologicalSettings';
import ApiKeysSettings from './ApiKeysSettings';
import AppearanceSettings from './AppearanceSettings';
import NotificationSettings from './NotificationSettings';
import AccountSettings from './AccountSettings';
import OrganizationSettings from '../organizations/OrganizationSettings';

type SettingsTab = {
  id: 'profile' | 'theological' | 'api-keys' | 'appearance' | 'notifications' | 'account' | 'organization';
  label: string;
  icon: JSX.Element;
  requiresRole?: 'org_admin';  // Only show for org_admin users
};

const SETTINGS_TABS: SettingsTab[] = [
  {
    id: 'profile',
    label: 'Profile',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    ),
  },
  {
    id: 'theological',
    label: 'Theological',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
      </svg>
    ),
  },
  // BYOK API Keys — hidden from end users (never specified as user-facing feature)
  // {
  //   id: 'api-keys',
  //   label: 'API Keys',
  //   icon: (
  //     <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
  //       <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
  //     </svg>
  //   ),
  // },
  {
    id: 'appearance',
    label: 'Appearance',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
      </svg>
    ),
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      </svg>
    ),
  },
  {
    id: 'organization',
    label: 'Organization',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
    requiresRole: 'org_admin',
  },
  {
    id: 'account',
    label: 'Account',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
      </svg>
    ),
  },
];

export default function SettingsModal() {
  const { isOpen, activeTab, closeSettings, setActiveTab } = useSettingsStore();
  const { user, tierInfo, loadTierInfo } = useAuthStore();

  // Load tier info on mount if not already loaded
  React.useEffect(() => {
    if (isOpen && !tierInfo) {
      loadTierInfo();
    }
  }, [isOpen, tierInfo, loadTierInfo]);

  // Filter tabs based on tier type
  const availableTabs = SETTINGS_TABS.filter(tab => {
    if (tab.requiresRole === 'org_admin') {
      // Show organization tab for users with organization tier type
      return tierInfo?.tier_type === 'organization';
    }
    return true;
  });

  const renderContent = () => {
    switch (activeTab) {
      case 'profile':
        return <ProfileSettings />;
      case 'theological':
        return <TheologicalSettings />;
      case 'api-keys':
        return <ApiKeysSettings />;
      case 'appearance':
        return <AppearanceSettings />;
      case 'notifications':
        return <NotificationSettings />;
      case 'organization':
        return user?.id ? <OrganizationSettings userId={user.id} /> : <div className="p-6">Loading...</div>;
      case 'account':
        return <AccountSettings />;
      default:
        return <ProfileSettings />;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={closeSettings} className="max-w-4xl w-full h-[90vh] sm:h-[85vh]">
      <div className="flex flex-col md:flex-row h-full">
        {/* Mobile Header + Tabs */}
        <div className="md:hidden border-b border-dark-border/50 p-3 sm:p-4">
          <h2 className="text-xl font-bold text-dark-text mb-3">Settings</h2>

          {/* Horizontal scrolling tabs for mobile */}
          <nav className="flex overflow-x-auto gap-2 pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap flex-shrink-0 min-h-[44px] transition-colors
                  ${
                    activeTab === tab.id
                      ? 'bg-accent-blue text-white'
                      : 'text-dark-text-muted bg-dark-card border border-dark-border hover:text-dark-text active:bg-dark-border'
                  }
                `}
              >
                {tab.icon}
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Desktop Sidebar Navigation */}
        <div className="hidden md:flex w-56 lg:w-64 border-r border-dark-border/50 p-4 lg:p-6 flex-col">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-dark-text">Settings</h2>
          </div>

          <nav className="flex-1 space-y-1">
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  w-full flex items-center gap-3 px-3 lg:px-4 py-3 rounded-lg text-left transition-colors min-h-[44px]
                  ${
                    activeTab === tab.id
                      ? 'bg-accent-blue/10 text-accent-blue'
                      : 'text-dark-text-muted hover:text-dark-text hover:bg-dark-bg/30 active:bg-dark-bg/50'
                  }
                `}
              >
                {tab.icon}
                <span className="font-medium">{tab.label}</span>
              </button>
            ))}
          </nav>

          {/* Close button at bottom */}
          <button
            onClick={closeSettings}
            className="mt-6 w-full py-3 px-3 lg:px-4 rounded-lg border border-dark-border/50 text-dark-text-muted hover:text-dark-text hover:bg-dark-bg/30 active:bg-dark-bg/50 transition-colors min-h-[44px]"
          >
            Close
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>

        {/* Mobile Close Button */}
        <div className="md:hidden border-t border-dark-border/50 p-3 sm:p-4" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
          <button
            onClick={closeSettings}
            className="w-full py-3 px-4 rounded-lg bg-accent-blue text-white font-medium hover:opacity-90 active:opacity-80 transition-opacity min-h-[44px]"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
